import { capitalize, LinearProgress, Tooltip } from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CardContent from "@mui/material/CardContent";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import SvgIcon from "@mui/material/SvgIcon";
import Switch from "@mui/material/Switch";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import ArrowRightIcon from "@untitled-ui/icons-react/build/esm/ArrowRight";
import Image01Icon from "@untitled-ui/icons-react/build/esm/Image01";
import PropTypes from "prop-types";
import type { ChangeEvent, FC, MouseEvent } from "react";
import { Fragment, useCallback, useState } from "react";
import { toast } from "react-hot-toast";
import { Scrollbar } from "src/components/scrollbar";
import { SeverityPill } from "src/components/severity-pill";
import { formatDateGitHub } from "src/pages/dashboard/helpers/datehelpers";
import { Stats } from "./useGitHubStats";
import FacebookIcon from "@mui/icons-material/Facebook";
import GitHubIcon from "@mui/icons-material/GitHub";
import { InfoCircle } from "@untitled-ui/icons-react";
import InfoIcon from "@mui/icons-material/Info";

interface CategoryOption {
  label: string;
  value: string;
}

const categoryOptions: CategoryOption[] = [
  {
    label: "Healthcare",
    value: "healthcare",
  },
  {
    label: "Makeup",
    value: "makeup",
  },
  {
    label: "Dress",
    value: "dress",
  },
  {
    label: "Skincare",
    value: "skincare",
  },
  {
    label: "Jewelry",
    value: "jewelry",
  },
  {
    label: "Blouse",
    value: "blouse",
  },
];

const safetyLevelDescriptions = [
  "Scientific research",
  "Certified body approval",
  "Close monitoring necessary, only use if you know what you are doing",
  "In very early phase of development or not recommended",
];

interface ProductListTableProps {
  count?: number;
  items?: Stats[];
  onPageChange?: (
    event: MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => void;
  onRowsPerPageChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  page?: number;
  rowsPerPage?: number;
  loading?: boolean;
}

export const ProductListTable: FC<ProductListTableProps> = (props) => {
  const {
    count = 0,
    items = [],
    onPageChange = () => {},
    onRowsPerPageChange,
    page = 0,
    rowsPerPage = 0,
    loading,
  } = props;
  const [currentProduct, setCurrentProduct] = useState<string | null>(null);

  const handleProductToggle = useCallback((productId: string): void => {
    setCurrentProduct((prevProductId) => {
      if (prevProductId === productId) {
        return null;
      }

      return productId;
    });
  }, []);

  const handleProductClose = useCallback((): void => {
    setCurrentProduct(null);
  }, []);

  const handleProductUpdate = useCallback((): void => {
    setCurrentProduct(null);
    toast.success("Product updated");
  }, []);

  const handleProductDelete = useCallback((): void => {
    toast.error("Product cannot be deleted");
  }, []);

  return (
    <div>
      <Scrollbar>
        <Table sx={{ minWidth: 1200 }}>
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell width="25%">Name</TableCell>
              <TableCell width="10%">Version</TableCell>
              <TableCell>
                <Box
                  sx={{
                    alignItems: "center",
                    display: "flex",
                  }}
                >
                  {"Safety"}
                  <Tooltip
                    title={
                      <Typography variant="body2">
                        {"Safety level of the app:"}
                        {safetyLevelDescriptions.map((description, ind) => (
                          <Typography key={description} variant="body2">
                            {" " + ind + " - " + description}
                          </Typography>
                        ))}
                      </Typography>
                    }
                  >
                    <InfoIcon />
                  </Tooltip>
                </Box>
              </TableCell>
              <TableCell>Last update</TableCell>
              <TableCell>Contributors</TableCell>
              <TableCell>Device</TableCell>
              <TableCell align="right">Links</TableCell>
            </TableRow>
          </TableHead>
          {loading ? (
            <LinearProgress
              sx={{
                position: "absolute",
                bottom: 0,
                left: 0,
                width: "100%",
              }}
            />
          ) : (
            <TableBody>
              {items.map((githubStat) => {
                const isCurrent = githubStat.id === currentProduct;
                const statusColor =
                  githubStat.device === "ios"
                    ? "info"
                    : githubStat.device === "web"
                    ? "success"
                    : githubStat.device === "web/desktop"
                    ? "warning"
                    : githubStat.device === "android"
                    ? "error"
                    : "primary";

                const safetyLevelColor =
                  githubStat.safetyLevel === 3
                    ? "error"
                    : githubStat.safetyLevel === 2
                    ? "info"
                    : "success";
                return (
                  <Fragment key={githubStat.id}>
                    <TableRow hover key={githubStat.id}>
                      <TableCell
                        padding="checkbox"
                        sx={{
                          ...(isCurrent && {
                            position: "relative",
                            "&:after": {
                              position: "absolute",
                              content: '" "',
                              top: 0,
                              left: 0,
                              backgroundColor: "primary.main",
                              width: 3,
                              height: "calc(100% + 1px)",
                            },
                          }),
                        }}
                        width="25%"
                      >
                        {/* TO DO make accessible for admins */}
                        {/* <IconButton
                          onClick={() => handleProductToggle(githubStat.id)}
                        >
                          <SvgIcon>
                            {isCurrent ? (
                              <ChevronDownIcon />
                            ) : (
                              <ChevronRightIcon />
                            )}
                          </SvgIcon>
                        </IconButton> */}
                      </TableCell>
                      <TableCell width="25%">
                        <Box
                          sx={{
                            alignItems: "center",
                            display: "flex",
                          }}
                        >
                          {githubStat.avatarUrl ? (
                            <Box
                              sx={{
                                alignItems: "center",
                                backgroundColor: "neutral.50",
                                backgroundImage: `url(${githubStat.avatarUrl})`,
                                backgroundPosition: "center",
                                backgroundSize: "cover",
                                borderRadius: 5,
                                display: "flex",
                                height: 80,
                                justifyContent: "center",
                                overflow: "hidden",
                                width: 80,
                              }}
                            />
                          ) : (
                            <Box
                              sx={{
                                alignItems: "center",
                                backgroundColor: "neutral.50",
                                borderRadius: 1,
                                display: "flex",
                                height: 80,
                                justifyContent: "center",
                                width: 80,
                              }}
                            >
                              <SvgIcon>
                                <Image01Icon />
                              </SvgIcon>
                            </Box>
                          )}
                          <Box
                            sx={{
                              cursor: "pointer",
                              ml: 2,
                            }}
                          >
                            <Typography variant="subtitle2">
                              {capitalize(githubStat.repo)}
                            </Typography>
                            <Typography color="text.secondary" variant="body2">
                              {capitalize(githubStat.owner)}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell width="10%">
                        <Typography variant="subtitle2">
                          {githubStat.latestRelease}
                        </Typography>
                        <Typography color="text.secondary" variant="body2">
                          {githubStat.releaseName}
                        </Typography>
                        <Typography color="text.secondary" variant="body2">
                          {"Release date: " + githubStat.publishedAt &&
                            formatDateGitHub(githubStat.publishedAt)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <SeverityPill color={safetyLevelColor}>
                          {githubStat.safetyLevel}
                        </SeverityPill>
                      </TableCell>
                      <TableCell>
                        {formatDateGitHub(githubStat.lastCommitDate)}
                      </TableCell>
                      <TableCell>{githubStat.mentionableUsersCount}</TableCell>
                      <TableCell>
                        <SeverityPill color={statusColor}>
                          {githubStat.device}
                        </SeverityPill>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip
                          title={
                            <Typography variant="body2">
                              Visite Facebook Group page
                            </Typography>
                          }
                        >
                          <IconButton
                            target="_blank"
                            href={githubStat?.facebook ?? ""}
                            disabled={!Boolean(githubStat?.facebook)}
                          >
                            <SvgIcon>
                              <FacebookIcon />
                            </SvgIcon>
                          </IconButton>
                        </Tooltip>
                        <Tooltip
                          title={
                            <Typography variant="body2">
                              Open in GitHub
                            </Typography>
                          }
                        >
                          <IconButton target="_blank" href={githubStat.url}>
                            <SvgIcon>
                              <GitHubIcon />
                            </SvgIcon>
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                    {isCurrent && (
                      <TableRow>
                        <TableCell
                          colSpan={7}
                          sx={{
                            p: 0,
                            position: "relative",
                            "&:after": {
                              position: "absolute",
                              content: '" "',
                              top: 0,
                              left: 0,
                              backgroundColor: "primary.main",
                              width: 3,
                              height: "calc(100% + 1px)",
                            },
                          }}
                        >
                          <CardContent>
                            <Grid container spacing={3}>
                              <Grid item md={6} xs={12}>
                                <Typography variant="h6">
                                  Basic details
                                </Typography>
                                <Divider sx={{ my: 2 }} />
                                <Grid container spacing={3}>
                                  <Grid item md={6} xs={12}>
                                    <TextField
                                      defaultValue={githubStat.name}
                                      fullWidth
                                      label="Product name"
                                      name="name"
                                    />
                                  </Grid>
                                  <Grid item md={6} xs={12}>
                                    <TextField
                                      defaultValue={"Test"}
                                      disabled
                                      fullWidth
                                      label="SKU"
                                      name="sku"
                                    />
                                  </Grid>
                                  <Grid item md={6} xs={12}>
                                    <TextField
                                      defaultValue={"test1"}
                                      fullWidth
                                      label="Category"
                                      select
                                    >
                                      {categoryOptions.map((option) => (
                                        <MenuItem
                                          key={option.value}
                                          value={option.value}
                                        >
                                          {option.label}
                                        </MenuItem>
                                      ))}
                                    </TextField>
                                  </Grid>
                                  <Grid item md={6} xs={12}>
                                    <TextField
                                      defaultValue={githubStat.id}
                                      disabled
                                      fullWidth
                                      label="Barcode"
                                      name="barcode"
                                    />
                                  </Grid>
                                </Grid>
                              </Grid>
                              <Grid item md={6} xs={12}>
                                <Typography variant="h6">
                                  Pricing and stocks
                                </Typography>
                                <Divider sx={{ my: 2 }} />
                                <Grid container spacing={3}>
                                  <Grid item md={6} xs={12}>
                                    <TextField
                                      defaultValue={"price test"}
                                      fullWidth
                                      label="Old price"
                                      name="old-price"
                                      InputProps={{
                                        startAdornment: (
                                          <InputAdornment position="start">
                                            {"currency test"}
                                          </InputAdornment>
                                        ),
                                      }}
                                      type="number"
                                    />
                                  </Grid>
                                  <Grid item md={6} xs={12}>
                                    <TextField
                                      defaultValue={"price test"}
                                      fullWidth
                                      label="New price"
                                      name="new-price"
                                      InputProps={{
                                        startAdornment: (
                                          <InputAdornment position="start">
                                            $
                                          </InputAdornment>
                                        ),
                                      }}
                                      type="number"
                                    />
                                  </Grid>
                                  <Grid
                                    item
                                    md={6}
                                    xs={12}
                                    sx={{
                                      alignItems: "center",
                                      display: "flex",
                                    }}
                                  >
                                    <Switch />
                                    <Typography variant="subtitle2">
                                      Keep selling when stock is empty
                                    </Typography>
                                  </Grid>
                                </Grid>
                              </Grid>
                            </Grid>
                          </CardContent>
                          <Divider />
                          <Stack
                            alignItems="center"
                            direction="row"
                            justifyContent="space-between"
                            sx={{ p: 2 }}
                          >
                            <Stack
                              alignItems="center"
                              direction="row"
                              spacing={2}
                            >
                              <Button
                                onClick={handleProductUpdate}
                                type="submit"
                                variant="contained"
                              >
                                Update
                              </Button>
                              <Button
                                color="inherit"
                                onClick={handleProductClose}
                              >
                                Cancel
                              </Button>
                            </Stack>
                            <div>
                              <Button
                                onClick={handleProductDelete}
                                color="error"
                              >
                                Delete product
                              </Button>
                            </div>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                );
              })}
            </TableBody>
          )}
        </Table>
      </Scrollbar>
      {/* <TablePagination
        component="div"
        count={count}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
        page={page}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={[5, 10, 25]}
      /> */}
    </div>
  );
};

ProductListTable.propTypes = {
  count: PropTypes.number,
  items: PropTypes.array,
  onPageChange: PropTypes.func,
  onRowsPerPageChange: PropTypes.func,
  page: PropTypes.number,
  rowsPerPage: PropTypes.number,
};
